import _ from 'lodash';
import { matchSorter, rankings } from 'match-sorter';

import { SearchCategory, SLASH_COMMAND_PREFIX } from './UniversalSearch.constants';

export const buildActionsIndex = ({ loanActions }) => {
  if (!loanActions?.length) {
    return [];
  }

  return loanActions.map(action => ({
    id: `action-${action.id}`,
    label: action.name,
    category: SearchCategory.ACTIONS,
    actionId: action.id,
    actionName: action.name,
  }));
};

const CONTEXT_BOOST_SCORE = 10;
const getCombinedSearchText = item =>
  [item.breadcrumb, item.label, item.fieldValue].filter(Boolean).join(' ');

const MATCH_SORTER_KEYS = [
  { key: 'label', threshold: rankings.MATCHES },
  { key: 'fieldValue', threshold: rankings.CONTAINS, maxRanking: rankings.CONTAINS },
  { key: 'breadcrumb', threshold: rankings.CONTAINS, maxRanking: rankings.CONTAINS },
  {
    key: getCombinedSearchText,
    threshold: rankings.CONTAINS,
    maxRanking: rankings.CONTAINS,
  },
];

export const filterSearchResults = ({ items, query, currentTabRoute, currentSubtab }) => {
  if (!query?.trim()) {
    return [];
  }

  const trimmedQuery = query.trim();
  const isSlashCommand = trimmedQuery.startsWith(SLASH_COMMAND_PREFIX);
  const effectiveQuery = isSlashCommand ? trimmedQuery.slice(1).trim() : trimmedQuery;

  if (isSlashCommand && !effectiveQuery) {
    return items
      .filter(item => item.category === SearchCategory.ACTIONS)
      .map(item => ({ ...item, score: 50 }));
  }

  const contextFiltered = items.filter(item => {
    if (currentSubtab && !_.isNil(item.entityType) && item.entityType !== currentSubtab) {
      return false;
    }

    return true;
  });

  const searchableItems = isSlashCommand
    ? contextFiltered.filter(item => item.category === SearchCategory.ACTIONS)
    : contextFiltered;

  const matched = matchSorter(searchableItems, effectiveQuery, {
    keys: MATCH_SORTER_KEYS,
    threshold: rankings.MATCHES,
  });

  return matched.map((item, index) => {
    const contextBoost =
      currentTabRoute && item.tabRoute === currentTabRoute ? CONTEXT_BOOST_SCORE : 0;
    const positionScore = matched.length - index;

    return { ...item, score: positionScore + contextBoost };
  });
};

export const groupResultsByCategory = results =>
  results.reduce((acc, item) => {
    const categoryItems = acc[item.category] || [];

    return {
      ...acc,
      [item.category]: [...categoryItems, item],
    };
  }, {});

const getRecordGroupKey = item => item.recordType || item.borrowerSection || 'other';

export const sortSuggestedItems = ({ filtered, category, currentTabRoute }) => {
  if (category === SearchCategory.FIELDS) {
    return _.sortBy(filtered, [
      item => (item.fieldValue ? 0 : 1),
      item => (currentTabRoute && item.tabRoute === currentTabRoute ? 0 : 1),
    ]);
  }

  if (category === SearchCategory.RECORDS) {
    return _.sortBy(filtered, [
      item => (item.isRecordField ? 1 : 0),
      item => (currentTabRoute && item.tabRoute === currentTabRoute ? 0 : 1),
    ]);
  }

  return currentTabRoute
    ? _.sortBy(filtered, item => (item.tabRoute === currentTabRoute ? 0 : 1))
    : filtered;
};

export const pickDiverseFields = (sortedFields, limit) => {
  const result = [];
  const seenSections = new Set();

  sortedFields.forEach(field => {
    if (result.length >= limit) {
      return;
    }

    const section = field.breadcrumb || field.sectionHref || '';

    if (!seenSections.has(section)) {
      result.push(field);
      seenSections.add(section);
    }
  });

  if (result.length < limit) {
    sortedFields
      .filter(field => !result.includes(field))
      .slice(0, limit - result.length)
      .forEach(field => {
        result.push(field);
      });
  }

  return result.slice(0, limit);
};

export const diverseTruncate = ({ items, limit }) => {
  const grouped = _.groupBy(items, getRecordGroupKey);
  const groupKeys = Object.keys(grouped);
  const result = [];
  const totalGroups = groupKeys.length;

  if (!totalGroups) {
    return [];
  }

  const perGroupMin = Math.max(1, Math.floor(limit / totalGroups));

  groupKeys.forEach(key => {
    const groupItems = grouped[key];
    const take = Math.min(perGroupMin, groupItems.length);

    result.push(...groupItems.slice(0, take));
  });

  if (result.length < limit) {
    const remaining = items.filter(item => !result.includes(item));
    result.push(...remaining.slice(0, limit - result.length));
  }

  return result.slice(0, limit);
};
